// src/components/homepage/ServicesSection.jsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { fadeIn } from '../../utils/gsapAnimations';
import Button from '../common/Button';
import { Link } from 'react-router-dom';
import SectionLoader from '../common/SectionLoader';
import { useContent } from '../../context/ContentContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

gsap.registerPlugin(Draggable, InertiaPlugin);

function ServicesSection() {
  const { content, errors, loading: globalLoading } = useContent();

  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const containerRef = useRef(null);
  const loopRef = useRef(null);

  const services = content?.services?.services || [];
  const duplicated = [...services, ...services]; // duplicate for infinite scroll

  useEffect(() => {
    if (sectionRef.current) fadeIn(sectionRef.current, 0.5);
  }, []);

  useEffect(() => {
    if (globalLoading || services.length === 0) return;
    if (!trackRef.current || !containerRef.current) return;

    const track = trackRef.current;
    const container = containerRef.current;
    const cards = Array.from(track.children);

    const itemWidth =
      cards[0].offsetWidth +
      parseFloat(getComputedStyle(cards[0]).marginRight || 0);
    const totalWidth = itemWidth * services.length;

    gsap.set(track, { x: 0 });

    // Infinite auto-scroll
    function autoScroll() {
      gsap.to(track, {
        x: `-=${0.5}`, // scroll speed
        duration: 0.016,
        ease: 'none',
        modifiers: {
          x: (x) => gsap.utils.wrap(-totalWidth, 0, parseFloat(x)) + 'px',
        },
        onComplete: autoScroll,
      });
    }

    autoScroll();

    // Draggable with inertia
    Draggable.create(track, {
      type: 'x',
      inertia: true,
      onPress: () => gsap.globalTimeline.pause(),
      onDrag: function () {
        gsap.set(track, { x: gsap.utils.wrap(-totalWidth, 0, this.x) });
      },
      onThrowUpdate: function () {
        gsap.set(track, { x: gsap.utils.wrap(-totalWidth, 0, this.x) });
      },
      onRelease: () => gsap.globalTimeline.resume(),
    });

    // Hover pause
    const handlePause = () => gsap.globalTimeline.pause();
    const handleResume = () => gsap.globalTimeline.resume();
    container.addEventListener('mouseenter', handlePause);
    container.addEventListener('mouseleave', handleResume);

    loopRef.current = {
      next: () => move(1),
      previous: () => move(-1),
    };

    function move(direction) {
      gsap.to(track, {
        x: `+=${-direction * itemWidth}`,
        duration: 0.6,
        ease: 'power2.out',
        modifiers: {
          x: (x) => gsap.utils.wrap(-totalWidth, 0, parseFloat(x)) + 'px',
        },
      });
    }

    return () => {
      container.removeEventListener('mouseenter', handlePause);
      container.removeEventListener('mouseleave', handleResume);
    };
  }, [globalLoading, services.length]);

  const movePrev = () => loopRef.current?.previous?.();
  const moveNext = () => loopRef.current?.next?.();

  if (globalLoading)
    return <SectionLoader message="Loading our services..." />;

  const servicesError =
    errors?.services || (services.length === 0 ? 'No services available' : null);

  if (servicesError) {
    return (
      <section ref={sectionRef} className="py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-lg text-gray-600">{servicesError}</p>
        </div>
      </section>
    );
  }

  const showArrows = services.length > 2;

  return (
    <section
      ref={sectionRef}
      className="py-16 bg-cream overflow-hidden relative"
    >
      <div className="max-w-7xl mx-auto px-6 relative">
        <h2 className="text-3xl md:text-4xl font-playfair-display font-semibold text-primary text-center mb-12">
          Our Services
        </h2>

        {showArrows && (
          <>
            <button
              onClick={movePrev}
              className="hidden lg:flex items-center justify-center absolute top-1/2 -translate-y-1/2 left-0 z-20 w-14 h-14 bg-white rounded-full shadow-lg hover:scale-110 transition text-primary text-2xl"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <button
              onClick={moveNext}
              className="hidden lg:flex items-center justify-center absolute top-1/2 -translate-y-1/2 right-0 z-20 w-14 h-14 bg-white rounded-full shadow-lg hover:scale-110 transition text-primary text-2xl"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </>
        )}

        <div
          ref={containerRef}
          className="relative overflow-hidden pb-6 cursor-grab active:cursor-grabbing touch-action-pan-y"
        >
          <div
            ref={trackRef}
            className="flex gap-8 will-change-transform"
            style={{ transform: 'translate3d(0,0,0)' }}
          >
            {duplicated.map((service, index) => (
              <div
                key={`${service.id}-${index}`}
                className="flex-shrink-0 w-72 sm:w-80 lg:w-96 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col touch-none"
              >
                {service.image && (
                  <img
                    src={`${process.env.REACT_APP_IMAGE_URL}${service.image}`}
                    alt={service.title}
                    className="w-full h-48 sm:h-56 object-cover"
                    loading="lazy"
                  />
                )}

                <div className="p-5 sm:p-6 flex flex-col flex-grow">
                  <h3 className="text-lg sm:text-xl font-playfair-display font-semibold text-primary mb-3 line-clamp-2">
                    {service.title}
                  </h3>

                  <p className="text-sm sm:text-base text-secondary font-montserrat flex-grow break-words">
                    {service.description}
                  </p>

                  {Boolean(service.contact_enabled) && (
                    <div className="mt-5 pt-2 border-t border-gray-100">
                      <Link to="/contact">
                        <Button className="bg-primary text-cream px-6 py-2.5 text-sm font-montserrat rounded-full hover:bg-primary/90 transition-all w-full sm:w-auto">
                          Reach Out
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ServicesSection;
